import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { AlertCircle, Loader2, Info, ArrowLeftRight, FileText, CheckSquare, File, Pill } from 'lucide-react';
import coverageApiService from '../../../services/coverage-api.service';
import { mapCoverageResponse } from '../../../utils/coverageMapper';
import { mapAlternativesResponse, mapTherapeuticAlternativesResponse } from '../../../utils/alternativesMapper';
import { useCoverageVerification } from '../../../contexts/CoverageVerificationContext';
import { cn } from '../../../lib/utils';
import InputBarSection from './InputBarSection';
import IntegratedDrugHeader from './IntegratedDrugHeader';
import { CoverageSummaryTabs } from './CoverageSummaryTabs';
import CoverageCriteriaTab from './CoverageCriteriaTab';
import AlternativesTab from './AlternativesTab';
import NotesTab from './NotesTab';
import DocumentsTab from './DocumentsTab';
import DrugDetailsTab from './DrugDetailsTab';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * UnifiedCoverageContainer Component
 * Single cohesive container following OrganizationSettings pattern
 * Consolidates InputBar, DrugHeader, and all tabs into unified layout
 * Uses CoverageVerificationContext for persistent state across navigation
 */
const UnifiedCoverageContainer = () => {
  // Get state and setters from context
  const {
    searchParams,
    coverageData,
    coverageError,
    indications,
    indicationsLoading,
    alternatives,
    therapeuticAlternatives,
    alternativesLoading,
    therapeuticAlternativesLoading,
    alternativesError,
    therapeuticAlternativesError,
    activeTab,
    apiCache,
    currentCacheKey,
    setSearchParams,
    setCoverageData,
    setCoverageError,
    setIndications,
    setIndicationsLoading,
    setAlternatives,
    setTherapeuticAlternatives,
    setAlternativesLoading,
    setTherapeuticAlternativesLoading,
    setAlternativesError,
    setTherapeuticAlternativesError,
    setActiveTab,
    setApiCache,
    setCurrentCacheKey,
  } = useCoverageVerification();

  // Local loading state (not persisted)
  const [loading, setLoading] = useState(false);

  // Sticky header state (local UI state, not persisted)
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  // Track if user is actively typing to prevent auto-scroll
  const isTypingRef = useRef(false);

  // Analytics timing state
  const [searchStartTime, setSearchStartTime] = useState(null);
  const [tabStartTime, setTabStartTime] = useState(Date.now());
  const [tabViewHistory, setTabViewHistory] = useState([]);

  // Get user info for analytics
  const userAttributes = useSelector((state) => state.auth.userAttributes);

  /**
   * Scroll to top on new search (only when not typing)
   */
  useEffect(() => {
    if (coverageData && !isTypingRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [coverageData]);

  /**
   * Scroll to top when switching tabs
   */
  useEffect(() => {
    if (coverageData && activeTab) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  /**
   * Generate cache key from search parameters
   */
  const generateCacheKey = (planId, productId, formularyId) => {
    return `${planId}_${productId}_${formularyId || 'noFormulary'}`;
  };

  /**
   * Handle search submission from InputBarSection
   * Fetches coverage data, indications, and alternatives in parallel
   */
  const handleSearch = async (params) => {
    // Mark that user submitted search (not typing), allow scroll to top
    isTypingRef.current = false;
    
    const checkStartTime = Date.now();
    setSearchStartTime(checkStartTime);
    
    setLoading(true);
    setCoverageError(null);
    setCoverageData(null);
    setSearchParams(params);
    setIndications([]);
    setAlternatives([]);
    setTherapeuticAlternatives(null);
    setActiveTab('summary'); // Reset to summary tab on new search
    setTabStartTime(Date.now()); // Reset tab timing
    setTabViewHistory(['summary']); // Reset tab history

    // Track coverage check initiation
    try {
      await sendToKinesis('coverage_events', 'COVERAGE_CHECK_INITIATED', {
        userid: userAttributes?.sub || 'anonymous',
        organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
        practice_id: userAttributes?.['custom:organization_id'] || '',
        practice_name: userAttributes?.['custom:org_name'] || '',
        session_id: window.currentSessionId || '',
        initiated_by_role: userAttributes?.['custom:org_user_title'] || '',
        completed_by_role: userAttributes?.['custom:org_user_title'] || '',
        planId: params.planId,
        planName: params.planName,
        payer: params.payer || '',
        pbm: params.pbm || '',
        controller: params.controller || '',
        drugId: params.drugId,
        drugName: params.drugMetadata?.drug_description || '',
        therapeuticClassId: params.drugMetadata?.therapeuticclass_id || null
      });
    } catch (analyticsError) {
      console.error('Analytics error (non-blocking):', analyticsError);
    }

    try {
      const planId = parseInt(params.planId);
      const productId = parseInt(params.drugId);

      if (isNaN(planId) || isNaN(productId)) {
        throw new Error('Invalid plan or drug ID');
      }

      // Fetch coverage data
      const response = await coverageApiService.getCoverage(planId, productId);

      if (!response || !response.result) {
        throw new Error('Failed to fetch coverage data');
      }

      const mappedData = mapCoverageResponse(response, params.drugMetadata);

      if (!mappedData) {
        setCoverageError(
          'Coverage information not provided for this combination'
        );
      } else {
        setCoverageData(mappedData);
        
        // Generate new cache key and clear cache if search parameters changed
        const newCacheKey = generateCacheKey(planId, productId, mappedData.formularyId);
        if (newCacheKey !== currentCacheKey) {
          console.log('[Cache] New search detected, clearing cache. New key:', newCacheKey);
          setApiCache({
            indications: null,
            indicationCriteria: {},
            coverageNotes: null,
            paNotes: null,
            stNotes: null,
            medicalNotesByIndication: {},
            medicalCoverageByIndication: {},
            formularyDocumentsByIndication: {},
            medicalDocumentsByIndication: {},
          });
          setCurrentCacheKey(newCacheKey);
        } else {
          console.log('[Cache] Same search parameters, keeping existing cache');
        }
      }

      // Fetch indications in parallel and cache them
      setIndicationsLoading(true);
      try {
        const indicationsResponse = await coverageApiService.getIndicationsByProduct(productId);
        if (indicationsResponse?.result?.Indications) {
          const fetchedIndications = indicationsResponse.result.Indications;
          setIndications(fetchedIndications);
          
          // Store in cache
          console.log('[Cache] Storing indications for productId:', productId);
          setApiCache(prev => ({
            ...prev,
            indications: fetchedIndications
          }));
        } else {
          setIndications([]);
          setApiCache(prev => ({
            ...prev,
            indications: []
          }));
        }
      } catch (indicationsError) {
        console.error('Error fetching indications:', indicationsError);
        setIndications([]);
      } finally {
        setIndicationsLoading(false);
      }

      // Fetch alternatives in parallel (cached for tab switching)
      setAlternativesLoading(true);
      setTherapeuticAlternativesLoading(true);

      try {
        const alternativesResponse = await coverageApiService.getAlternativeCoverage(planId, productId);
        const mappedAlternatives = mapAlternativesResponse(alternativesResponse);
        setAlternatives(mappedAlternatives || []);
        setAlternativesError(null);
      } catch (altError) {
        console.error('Error fetching alternatives:', altError);
        setAlternativesError('Failed to load covered alternatives');
        setAlternatives([]);
      } finally {
        setAlternativesLoading(false);
      }

      try {
        const therapeuticResponse = await coverageApiService.getTherapeuticAlternatives(productId);
        const originalTherapeuticClassId = params.drugMetadata?.therapeuticclass_id;
        const mappedTherapeutic = mapTherapeuticAlternativesResponse(therapeuticResponse, originalTherapeuticClassId);
        setTherapeuticAlternatives(mappedTherapeutic || []);
        setTherapeuticAlternativesError(null);
      } catch (therapeuticError) {
        console.error('Error fetching therapeutic alternatives:', therapeuticError);
        setTherapeuticAlternativesError('Failed to load therapeutic alternatives');
        setTherapeuticAlternatives([]);
      } finally {
        setTherapeuticAlternativesLoading(false);
      }

      // Track successful coverage check completion
      const apiEndTime = Date.now();
      // Count MMIT API calls made during this coverage check:
      // 1. getCoverage, 2. getIndicationsByProduct, 3. getAlternativeCoverage, 4. getTherapeuticAlternatives
      const mmitApiCallCount = 4;
      try {
        await sendToKinesis('coverage_events', 'COVERAGE_CHECK_COMPLETED', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          practice_id: userAttributes?.['custom:organization_id'] || '',
          practice_name: userAttributes?.['custom:org_name'] || '',
          session_id: window.currentSessionId || '',
          initiated_by_role: userAttributes?.['custom:org_user_title'] || '',
          completed_by_role: userAttributes?.['custom:org_user_title'] || '',
          last_api_call_timestamp: apiEndTime,
          mmit_api_call_count: mmitApiCallCount,
          planId: planId.toString(),
          planName: params.planName,
          drugId: productId.toString(),
          drugName: params.drugMetadata?.drug_description || '',
          
          // Plan/Payer details (MASTER-311)
          payer: params.payer || '',
          pbm: params.pbm || '',
          controller: params.controller || '',
          
          // Drug classification (MASTER-310)
          therapeuticClass: params.drugMetadata?.therapeuticclass || '',
          therapeuticClassId: params.drugMetadata?.therapeuticclass_id || null,
          
          // Coverage details
          pharmacyCoverageStatus: mappedData?.coverage?.status || 'unknown',
          pharmacyTier: mappedData?.coverage?.tier || '',
          requiresPriorAuth: mappedData?.coverage?.paRequired || false,
          requiresStepTherapy: mappedData?.coverage?.stRequired || false,
          requiresQuantityLimit: mappedData?.coverage?.qlApplies || false,
          
          // Medical coverage (MASTER-306 fix: use correct field path)
          hasMedicalCoverage: mappedData?.coverage?.medicalBenefit || false,
          
          // Counts
          indicationsCount: indications?.length || 0,
          indicationsList: indications?.map(i => i.indication_name) || [],
          coveredAlternativesCount: alternatives?.length || 0,
          therapeuticAlternativesCount: therapeuticAlternatives?.length || 0,
          
          // Availability
          hasNotes: mappedData?.notesAvailable || false,
          hasCriteria: (indications?.length || 0) > 0,
          hasDocuments: true,
          
          // Performance
          apiResponseTime: apiEndTime - checkStartTime,
          totalLoadTime: apiEndTime - checkStartTime
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }

    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setCoverageError(errorMessage);
      console.error('Coverage fetch error:', error);
      
      // Track failed coverage check
      try {
        await sendToKinesis('coverage_events', 'COVERAGE_CHECK_FAILED', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          practice_id: userAttributes?.['custom:organization_id'] || '',
          practice_name: userAttributes?.['custom:org_name'] || '',
          session_id: window.currentSessionId || '',
          initiated_by_role: userAttributes?.['custom:org_user_title'] || '',
          completed_by_role: userAttributes?.['custom:org_user_title'] || '',
          planId: params.planId,
          planName: params.planName || '',
          drugId: params.drugId,
          drugName: params.drugMetadata?.drug_description || '',
          // Plan/Payer details (MASTER-311)
          payer: params.payer || '',
          pbm: params.pbm || '',
          controller: params.controller || '',
          // Drug classification (MASTER-310)
          therapeuticClass: params.drugMetadata?.therapeuticclass || '',
          therapeuticClassId: params.drugMetadata?.therapeuticclass_id || null,
          // Error details
          errorType: 'api_error',
          errorMessage: errorMessage,
          failedEndpoint: 'coverage'
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle tab changes with analytics tracking
   */
  const handleTabChange = (newTab) => {
    const now = Date.now();
    const timeOnPreviousTab = now - tabStartTime;
    
    // Track tab view
    try {
      sendToKinesis('coverage_events', 'COVERAGE_TAB_VIEWED', {
        userid: userAttributes?.sub || 'anonymous',
        organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
        practice_id: userAttributes?.['custom:organization_id'] || '',
        practice_name: userAttributes?.['custom:org_name'] || '',
        session_id: window.currentSessionId || '',
        initiated_by_role: userAttributes?.['custom:org_user_title'] || '',
        feature_module: newTab,
        planId: searchParams?.planId || '',
        drugId: searchParams?.drugId || '',
        tabName: newTab,
        previousTab: activeTab,
        timeOnPreviousTab: timeOnPreviousTab,
        viewMethod: 'sidebar_click'
      });
    } catch (analyticsError) {
      console.error('Analytics error (non-blocking):', analyticsError);
    }
    
    setActiveTab(newTab);
    setTabStartTime(now);
    setTabViewHistory(prev => [...prev, newTab]);
  };

  /**
   * Handle tab change from mobile dropdown
   */
  const handleMobileTabChange = (newTab) => {
    const now = Date.now();
    const timeOnPreviousTab = now - tabStartTime;
    
    // Track tab view with mobile method
    try {
      sendToKinesis('coverage_events', 'COVERAGE_TAB_VIEWED', {
        userid: userAttributes?.sub || 'anonymous',
        organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
        practice_id: userAttributes?.['custom:organization_id'] || '',
        practice_name: userAttributes?.['custom:org_name'] || '',
        session_id: window.currentSessionId || '',
        initiated_by_role: userAttributes?.['custom:org_user_title'] || '',
        feature_module: newTab,
        planId: searchParams?.planId || '',
        drugId: searchParams?.drugId || '',
        tabName: newTab,
        previousTab: activeTab,
        timeOnPreviousTab: timeOnPreviousTab,
        viewMethod: 'mobile_dropdown'
      });
    } catch (analyticsError) {
      console.error('Analytics error (non-blocking):', analyticsError);
    }
    
    setActiveTab(newTab);
    setTabStartTime(now);
    setTabViewHistory(prev => [...prev, newTab]);
  };

  /**
   * Render content based on current state
   */
  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <Card className="shadow-sm w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
              <p className="text-base font-medium text-foreground">
                Loading coverage information...
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Error state
    if (coverageError) {
      return (
        <Card className="shadow-sm w-full">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm leading-relaxed">{coverageError}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      );
    }

    // Empty state - interface is self-evident through labels, placeholders, and button
    if (!coverageData) {
      return null;
    }

    // Results state - Content + Right Sidebar Layout (matches Drug Search)
    return (
      <div className="flex gap-6">
        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {/* Mobile Dropdown Navigation */}
          <div className="lg:hidden mb-4">
            <Select value={activeTab} onValueChange={handleMobileTabChange}>
              <SelectTrigger className="w-full text-sm font-semibold min-h-[48px] border-2" aria-label="Select coverage section">
                <SelectValue placeholder="Jump to section..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary" className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    <span>Coverage Summary</span>
                  </div>
                </SelectItem>
                <SelectItem value="alternatives" className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <ArrowLeftRight className="h-4 w-4" />
                    <span>Alternatives</span>
                  </div>
                </SelectItem>
                <SelectItem value="notes" className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Coverage Notes</span>
                  </div>
                </SelectItem>
                <SelectItem value="criteria" className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <CheckSquare className="h-4 w-4" />
                    <span>Coverage Criteria</span>
                  </div>
                </SelectItem>
                <SelectItem value="documents" className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <File className="h-4 w-4" />
                    <span>Documents</span>
                  </div>
                </SelectItem>
                {coverageData?.drug?.drugMedId && (
                  <SelectItem value="drug-details" className="text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4" />
                      <span>Drug Details</span>
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Tab Content */}
          {activeTab === 'summary' && (
            <div className="space-y-8">
              <CoverageSummaryTabs
                coverageData={{
                  ...coverageData,
                  medicalCoverageCache: apiCache.medicalCoverageByIndication,
                  onMedicalCoverageUpdate: (indicationId, data) => {
                    console.log('[Cache] Storing medical coverage for indication:', indicationId);
                    setApiCache(prev => ({
                      ...prev,
                      medicalCoverageByIndication: {
                        ...prev.medicalCoverageByIndication,
                        [indicationId]: data
                      }
                    }));
                  }
                }}
                productId={parseInt(searchParams.drugId)}
                planId={parseInt(searchParams.planId)}
                indications={indications}
                indicationsLoading={indicationsLoading}
              />
            </div>
          )}

          {activeTab === 'alternatives' && (
            <div className="space-y-8">
              <AlternativesTab
                alternatives={alternatives}
                therapeuticAlternatives={therapeuticAlternatives}
                alternativesLoading={alternativesLoading}
                therapeuticAlternativesLoading={therapeuticAlternativesLoading}
                alternativesError={alternativesError}
                therapeuticAlternativesError={therapeuticAlternativesError}
                originalTherapeuticClassId={searchParams?.drugMetadata?.therapeuticclass_id}
              />
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-8">
              <NotesTab
                noteId={coverageData.noteId}
                productId={parseInt(searchParams.drugId)}
                formularyId={coverageData.formularyId}
                notesAvailable={coverageData.notesAvailable || false}
                indications={indications}
                indicationsLoading={indicationsLoading}
                coverageStatus={coverageData.coverage?.status}
                paRequired={coverageData.coverage?.paRequired || false}
                stRequired={coverageData.coverage?.stRequired || false}
                cachedCoverageNotes={apiCache.coverageNotes}
                cachedPANotes={apiCache.paNotes}
                cachedSTNotes={apiCache.stNotes}
                cachedMedicalNotes={apiCache.medicalNotesByIndication}
                onNotesUpdate={(key, data) => {
                  console.log('[Cache] Storing notes data for key:', key);
                  if (typeof key === 'string') {
                    // Top-level cache keys (coverageNotes, paNotes, stNotes)
                    setApiCache(prev => ({
                      ...prev,
                      [key]: data
                    }));
                  } else if (typeof key === 'object' && key.indicationId) {
                    // Medical notes by indication
                    setApiCache(prev => ({
                      ...prev,
                      medicalNotesByIndication: {
                        ...prev.medicalNotesByIndication,
                        [key.indicationId]: data
                      }
                    }));
                  }
                }}
              />
            </div>
          )}

          {activeTab === 'criteria' && (
            <div className="space-y-8">
              {indicationsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                  <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    Loading coverage criteria...
                  </p>
                  <p className="text-xs text-muted-foreground text-center max-w-md">
                    Fetching coverage criteria by indication
                  </p>
                </div>
              ) : (
                <CoverageCriteriaTab
                  coverageData={{
                    ...coverageData,
                    indications: indications
                  }}
                  productId={parseInt(searchParams.drugId)}
                  formularyId={coverageData?.formularyId}
                  productName={searchParams?.drugMetadata?.drug_description || 'Unknown Product'}
                  planName={searchParams?.planName || 'Unknown Plan'}
                  indicationCriteriaCache={apiCache.indicationCriteria}
                  onIndicationCriteriaUpdate={(indicationId, data) => {
                    console.log('[Cache] Storing indication criteria for:', indicationId);
                    setApiCache(prev => ({
                      ...prev,
                      indicationCriteria: {
                        ...prev.indicationCriteria,
                        [indicationId]: data
                      }
                    }));
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-8">
              <DocumentsTab
                indications={indications}
                productId={coverageData.productId}
                formularyId={coverageData.formularyId}
                indicationsLoading={indicationsLoading}
                cachedFormularyDocuments={apiCache.formularyDocumentsByIndication}
                cachedMedicalDocuments={apiCache.medicalDocumentsByIndication}
                onDocumentsUpdate={(indicationId, type, data) => {
                  console.log('[Cache] Storing documents for indication:', indicationId, 'type:', type);
                  const cacheKey = type === 'formulary' 
                    ? 'formularyDocumentsByIndication' 
                    : 'medicalDocumentsByIndication';
                  setApiCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                      ...prev[cacheKey],
                      [indicationId]: data
                    }
                  }));
                }}
              />
            </div>
          )}

          {activeTab === 'drug-details' && coverageData?.drug?.drugMedId && (
            <div className="space-y-8">
              <DrugDetailsTab
                drugMedId={coverageData.drug.drugMedId}
                drugName={coverageData.drug.name}
              />
            </div>
          )}
        </div>

        {/* Right Sidebar Navigation (Desktop) */}
        <div className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-[200px] space-y-1">
            <h3 className="text-xs font-semibold text-muted-foreground mb-3 px-3 tracking-wide">
              JUMP TO
            </h3>
            <button
              onClick={() => handleTabChange('summary')}
              className={cn(
                'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                activeTab === 'summary'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'hover:bg-muted text-muted-foreground font-medium'
              )}
              aria-label="Coverage summary"
              aria-current={activeTab === 'summary' ? 'location' : undefined}
            >
              <Info className="h-4 w-4 shrink-0" />
              <span className="flex-1 break-words">Coverage Summary</span>
            </button>
            <button
              onClick={() => handleTabChange('alternatives')}
              className={cn(
                'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                activeTab === 'alternatives'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'hover:bg-muted text-muted-foreground font-medium'
              )}
              aria-label="Alternative medications"
              aria-current={activeTab === 'alternatives' ? 'location' : undefined}
            >
              <ArrowLeftRight className="h-4 w-4 shrink-0" />
              <span className="flex-1 break-words">Alternatives</span>
            </button>
            <button
              onClick={() => handleTabChange('notes')}
              className={cn(
                'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                activeTab === 'notes'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'hover:bg-muted text-muted-foreground font-medium'
              )}
              aria-label="Coverage notes"
              aria-current={activeTab === 'notes' ? 'location' : undefined}
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="flex-1 break-words">Coverage Notes</span>
            </button>
            <button
              onClick={() => handleTabChange('criteria')}
              className={cn(
                'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                activeTab === 'criteria'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'hover:bg-muted text-muted-foreground font-medium'
              )}
              aria-label="Coverage criteria"
              aria-current={activeTab === 'criteria' ? 'location' : undefined}
            >
              <CheckSquare className="h-4 w-4 shrink-0" />
              <span className="flex-1 break-words">Coverage Criteria</span>
            </button>
            <button
              onClick={() => handleTabChange('documents')}
              className={cn(
                'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                activeTab === 'documents'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'hover:bg-muted text-muted-foreground font-medium'
              )}
              aria-label="Related documents"
              aria-current={activeTab === 'documents' ? 'location' : undefined}
            >
              <File className="h-4 w-4 shrink-0" />
              <span className="flex-1 break-words">Documents</span>
            </button>
            {coverageData?.drug?.drugMedId && (
              <button
                onClick={() => handleTabChange('drug-details')}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 min-h-[44px]',
                  activeTab === 'drug-details'
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : 'hover:bg-muted text-muted-foreground font-medium'
                )}
                aria-label="Drug details"
                aria-current={activeTab === 'drug-details' ? 'location' : undefined}
              >
                <Pill className="h-4 w-4 shrink-0" />
                <span className="flex-1 break-words">Drug Details</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Page Header */}
        <div className="space-y-2 pb-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Coverage Verification
          </h1>
          <p className="text-sm font-normal text-muted-foreground leading-relaxed">
            Search and verify drug coverage across insurance plans
          </p>
        </div>

        {/* Search Input Section - Sticky with scroll lock */}
        <div className="sticky top-[64px] z-40 bg-white mb-6" style={{ contain: 'layout' }}>
          <Card className="shadow-md border" id="search-card">
            <CardContent className="p-4">
            <InputBarSection 
              onSearch={handleSearch}
              initialPlanId={searchParams?.planId}
              initialDrugId={searchParams?.drugId}
              initialPlanName={searchParams?.planName}
              initialDrugName={searchParams?.drugMetadata?.drug_description}
              initialDrugMetadata={searchParams?.drugMetadata}
            />
          </CardContent>
          </Card>
        </div>

        {/* Dynamic Content - Loading, Error, Empty, or Results */}
        {renderContent()}
      </div>
    </div>
  );
};

export default UnifiedCoverageContainer;
